import { ApolloClient, DocumentNode, ErrorLike } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';

import { completionMessage, getHumanReadableErrorMessage, getQueryName, isIntermittentNetworkError, logger, getSafePlaygroundLink, QueryResultWithErrors } from './feedback';
import { getEnvData, getSimplifiedDeployedEnvName } from '@/utils/env';

import { sha256 } from '../sha256';
import type { Sha256 } from '@/types/sha256';

const RETRY_LIMIT_BROWSER = 5;
const RETRY_LIMIT_SERVER = 3;
const RETRY_INITIAL_DELAY_BROWSER = 300;
const RETRY_INITIAL_DELAY_SERVER = 500;
const RETRY_MAX_DELAY = 1500;

const envData = getEnvData();
const simpleDeployedEnvName = getSimplifiedDeployedEnvName();
const localBackendCacheTimeInMinutes = parseInt(envData.GRAPHQL_LOCAL_DISKCACHE_TTL_MINUTES || '20');

const graphServerUrl = process.env.NEXT_PUBLIC_GRAPHQL_API_HOST;
const contentSrcEnv = process.env.NEXT_PUBLIC_CMS_GRAPH_CONTENT_SOURCE;
const forceCacheSkip = process.env.CMS_GRAPHQL_FORCE_CACHE_SKIP === 'true';
const diskCacheEnabled = envData.deployedEnvName.indexOf('local--') === 0 && envData.GRAPHQL_LOCAL_DISKCACHE === 'true';
const isCalledFromBrowser = typeof window !== 'undefined';
const isProd = () => envData.deployedEnvName === 'prod';
const isRunTimeOnServer = () => typeof process !== 'undefined' && process.env.npm_lifecycle_script?.indexOf('next build') === -1;

// Using GET requests means not using POST, which means there will not be caching at the CDN level.
// For now, we only want to use CDN cache (GET requests) on the client side or if: in production + on the server side + at run time (not build time).
// This reduces the amount of stale data that gets returned from GraphQL, limiting it to only when we could potentially bring Graph down with too much traffic.
const useGetRequests = isCalledFromBrowser || (isProd() && isRunTimeOnServer());

if (!graphServerUrl) {
  throw new Error('NEXT_PUBLIC_GRAPHQL_API_HOST environment variable is required.');
}

if (forceCacheSkip && isProd()) {
  throw new Error('CMS_GRAPHQL_FORCE_CACHE_SKIP environment variable cannot be set to true in production.');
}

const mergeAdditionalHeaders = (oldHeaders: object, additionalHeaders: object = {}) => {
  const newHeaders: {
    'x-pbs-kids-content-source-env'?: string;
    'x-pbs-kids-force-cache-skip'?: string;
  } = Object.assign(
    {},
    oldHeaders,
    additionalHeaders,
  );

  if (contentSrcEnv) {
    newHeaders['x-pbs-kids-content-source-env'] = contentSrcEnv;
  }

  if (forceCacheSkip) {
    newHeaders['x-pbs-kids-force-cache-skip'] = 'true';
  }

  return {
    headers: newHeaders,
  };
};

const clients: Array<{ key: string, client: ApolloClient }> = [];

const getClientIdentity = () => {
  const clientSource = isCalledFromBrowser ? 'browser' : 'server';
  const clientName = `pbs.kids.website.${simpleDeployedEnvName}.${clientSource}`;

  const clientVersion = JSON.stringify({
    semver: envData.packageVersion,
    commit: envData.buildId || 'n/a',
    built: envData.buildTime || 'n/a',
  });

  return {
    clientName,
    clientVersion,
  };
};

const { clientName, clientVersion } = getClientIdentity();

const getClient = async (additionalHeaders: { [key: string]: string } = {}) => {
  const clientKey = JSON.stringify(additionalHeaders);
  const existingClient = clients.find((client) => client.key === clientKey);
  if (existingClient) {
    return existingClient.client;
  }

  const { HttpLink, InMemoryCache } = await import('@apollo/client');
  const { PersistedQueryLink } = await import('@apollo/client/link/persisted-queries');
  const { SetContextLink } = await import('@apollo/client/link/context');
  const { VoidCache } = await import('./client-caching');

  const httpLink = new HttpLink({ uri: graphServerUrl });

  const authLink = new SetContextLink(({ headers }) => {
    return mergeAdditionalHeaders(headers, additionalHeaders);
  });

  const defaultOptions: ApolloClient.DefaultOptions = {
    watchQuery: {
      errorPolicy: 'all',
    },
    query: {
      errorPolicy: 'all',
    },
  };

  const persistedQueriesLink = new PersistedQueryLink({
    useGETForHashedQueries: useGetRequests,
    sha256: (sha256 as Sha256),
  });

  const retryLink = new RetryLink({
    delay: {
      // From docs @ <https://www.apollographql.com/docs/react/api/link/apollo-link-retry#avoiding-thundering-herd>:
      // Starting with initialDelay, the delay of each subsequent retry is increased exponentially, meaning it's multiplied
      // by 2 each time. For example, if initialDelay is 100, additional retries will occur after delays of 200, 400, 800, etc.
      initial: isCalledFromBrowser ? RETRY_INITIAL_DELAY_BROWSER : RETRY_INITIAL_DELAY_SERVER,
      jitter: true,
      max: RETRY_MAX_DELAY,
    },
    attempts: (attempt, operation, error) => {
      const canRetry = isIntermittentNetworkError(error);
      const retryLimit = isCalledFromBrowser ? RETRY_LIMIT_BROWSER : RETRY_LIMIT_SERVER;

      if (canRetry) {
        getSafePlaygroundLink(operation.query, operation.variables, additionalHeaders).then((playgroundUrl) => {
          logger.error(`RetryLink [ ${error.toString()} ] : Retrying query ${getQueryName(operation.query) || '[unspecified]'} (${attempt} / ${retryLimit})...\n\nTry it out in our GraphQL playground here:\n${playgroundUrl || '[Unavailable due to unresolved imports]'}`);
        });
      }

      return canRetry && attempt + 1 <= retryLimit;
    },
  });

  const link = persistedQueriesLink.concat(retryLink).concat(authLink).concat(httpLink);

  const clientOptions: ApolloClient.Options = {
    link,
    cache: additionalHeaders['x-pbs-kids-force-cache-skip'] === 'true' ? new VoidCache() : new InMemoryCache(),
    defaultOptions,
    clientAwareness: {
      name: clientName,
      version: clientVersion,
    },
  };

  if (!isCalledFromBrowser) {
    // Disable Apollo Client caching when called on the server side.
    clientOptions.cache = new VoidCache();

    clientOptions.defaultOptions = {
      watchQuery: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore',
      },
      query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      },
    };
  }

  const client = new ApolloClient(clientOptions);

  clients.push({ key: clientKey, client });

  return client;
};

const convertStringToDocNode = async (query: string) => {
  const { gql } = await import('@apollo/client');
  return gql(query);
};

function getGqlString(doc: DocumentNode) {
  return doc.loc && doc.loc.source.body;
}

const queryGraph = async (
  query: DocumentNode | string,
  variables?: object,
  additionalHeaders?: { [key: string]: string },
  onError?: (error: ErrorLike) => void,
) => {
  const operation: {
    query: DocumentNode,
    variables?: object,
  } = {
    query: typeof query === 'string' ? await convertStringToDocNode(query) : query,
    variables,
  };
  const now = Date.now();
  const client = await getClient(additionalHeaders);

  const handleFatalError = async (response: QueryResultWithErrors) => {
    const fatalError = new Error(
      `queryGraph() : ${JSON.stringify({ isCalledFromBrowser })} : ` + await getHumanReadableErrorMessage(response, query, variables, additionalHeaders, now),
    );
    if (typeof onError === 'function') {
      onError(fatalError);
    }
    throw fatalError;
  };

  return client
    .query(operation)
    .then(async (response) => {
      if (response.error) {
        if (!response.data) {
          await handleFatalError(response as QueryResultWithErrors);
        }
        if (typeof onError === 'function') {
          onError(response.error);
        } else {
          logger.debug(
            await getHumanReadableErrorMessage(
              response as QueryResultWithErrors,
              query,
              variables,
              additionalHeaders,
              now,
            ),
          );
        }
      }

      completionMessage(null, now, query, variables, additionalHeaders, response.data);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (response.data || {}) as any;
    });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
let diskCacheInstance: any;

const getDiskCacheInstance = async () => {
  if (diskCacheInstance) {
    return diskCacheInstance;
  }
  // Type definitions are not available for ttl-file-cache and
  // I'm not sure a better way than this to suppress the TS errors.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore-next-line
  const DiskCache = await import('ttl-file-cache');

  // This dynamic import is done because a normal import causes issues in the browser.
  // eslint-disable-next-line new-cap
  return diskCacheInstance = new DiskCache.default({
    // default dir is '/tmp/ttl-file-cache' or whatever os.tempdir() resolves to on your operating system
    dir: `/tmp/${envData.packageName}/utils.graphql/ttl-file-cache`,
  });
};

const getQueryCacheKey = async (
  query: DocumentNode | string,
  variables?: object,
  additionalHeaders?: { [key: string]: string },
) => {
  const queryAsString = typeof query === 'string' ? query : getGqlString(query)?.replace(/\s+/g, ' ');
  return sha256(JSON.stringify({
    queryAsString,
    variables,
    additionalHeaders,
    graphServerUrl,
    contentSrcEnv,
  }));
};

const queryGraphWithDiskCache = async (
  query: DocumentNode | string,
  variables?: object,
  additionalHeaders?: { [key: string]: string },
  onError?: (error: ErrorLike) => void,
) => {
  const cacheKey = await getQueryCacheKey(query, variables, additionalHeaders);
  const startTime = Date.now();
  const cache = await getDiskCacheInstance();
  const cachedResponse = cache.get(cacheKey);

  if (cachedResponse) {
    completionMessage(true, startTime, query, variables, additionalHeaders);
    return JSON.parse(cachedResponse.toString());
  }

  const response = await queryGraph(query, variables, additionalHeaders, onError);
  completionMessage(false, startTime, query, variables, additionalHeaders);
  await cache.set(cacheKey, JSON.stringify(response), (localBackendCacheTimeInMinutes * 60));
  return response;
};

const initProgressBar = async () => {
  if (typeof window !== 'undefined') {
    return await import('@/utils/progress-bar').then((module) => module.default);
  } else {
    return {
      start: () => {},
      complete: () => {},
    };
  }
};

const queryGraphClient = async (
  query: DocumentNode | string,
  variables?: object,
  additionalHeaders?: { [key: string]: string },
  onError?: (error: ErrorLike) => void,
) => {
  const now = Date.now();

  if (!isCalledFromBrowser) {
    throw new Error('queryGraphClient should only be called from a client. Use queryGraphServer instead.');
  }

  const progressBar = await initProgressBar();

  progressBar.start();

  const data = await queryGraph(query, variables, additionalHeaders, onError)
    .catch((error) => {
      progressBar.complete();
      if (onError && typeof onError === 'function') {
        // If an onError handler is provided, do not report
        // this error to Sentry and let the handler manage the error.
        return;
      }

      const humanReadable = getHumanReadableErrorMessage(error, query, variables, additionalHeaders, now);
      humanReadable.then((message) => {
        if (isIntermittentNetworkError(error)) {
          // If it's an intermittent network error, our RetryLink integration has already logged an error, so let's not dupe that.
          logger.debug(`queryGraphClient() : ${message}`);
        } else {
          logger.error(`queryGraphClient() : ${message}`);
        }

        throw new Error(error);
      });
    });
  progressBar.complete();

  return data;
};

const queryGraphServer = async (
  query: DocumentNode | string,
  variables?: object,
  additionalHeaders?: { [key: string]: string },
  onError?: (error: ErrorLike) => void,
  allowDiskCache = true,
) => {
  if (isCalledFromBrowser) {
    throw new Error('queryGraphServer should only be called from the server. Use queryGraphClient instead.');
  }
  return diskCacheEnabled && allowDiskCache ?
    queryGraphWithDiskCache(query, variables, additionalHeaders, onError) :
    queryGraph(query, variables, additionalHeaders, onError);
};

export {
  getGqlString,
  convertStringToDocNode,
  graphServerUrl,
  mergeAdditionalHeaders,
  queryGraphClient,
  queryGraphServer,
};
